import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, throwError, forkJoin } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { UserService } from "../../../../core/services/user.service";
import { SaveAgreementDTO } from "../../../../domain/models/agreement.model";
import { EditNotesUseCase } from "../../../../domain/use-cases/agreement/edit-notes.usecase";
import { LoadAgreementUseCase } from "../../../../domain/use-cases/agreement/load-agreement.usecase";
import { PublishOpportunityUseCase } from "../../../../domain/use-cases/agreement/publish-opportunity.usecase";
import { SaveAgreementUseCase } from "../../../../domain/use-cases/agreement/save.usecase";
import { ChangeStatusUseCase } from "../../../../domain/use-cases/proposal/change-status.usecase";
import { MonthlyCostNew } from "../components/header-dashboard/external-travel-cost-grid/external-travel-cost-grid.component";
import { ForumMessage } from "../components/header-dashboard/forum-panel/forum-panel.component";
import { Milestone } from "../components/header-dashboard/milestone-panel/milestones-grid/milestones-grid.component";
import { GridRow2 } from "./grid.controller";

@Injectable({
  providedIn: "root",
})
export class AgreementService {
  private readonly endpoint = environment.apiUrl;
  constructor(
    private loadAgreementUseCase: LoadAgreementUseCase,
    private editNotesUseCase: EditNotesUseCase,
    private changeStatusUseCase: ChangeStatusUseCase,
    private save: SaveAgreementUseCase,
    private publishOppo: PublishOpportunityUseCase,
    private http: HttpClient,
    private userService: UserService
  ) {}

  saveAgreement(agreementId: number, dataSource: GridRow2[]) {
    let indexesToRemove: Set<number> = new Set();
    dataSource.forEach((el, index) => {
      if (el.readonly) {
        indexesToRemove.add(index);
      }
    });
    console.log("INDEXES TO REMOVE", [...indexesToRemove]);

    let localRows = [
      ...dataSource.filter((el, index) => !indexesToRemove.has(index)),
    ];
    let smarts: SaveAgreementDTO[] = [];
    localRows.forEach((row) => {
      row.smarts.forEach((el) => {
        let s: SaveAgreementDTO = {
          discipplineId: row.disciplineId!,
          agreementId: agreementId,
          geoId: row.geoId!,
          numberOfHours: el.scheduledHours,
          professionalFigureId: row.figureId!,
          resourceId: row.resourceId,
          weekId: el.week.id,
        };
        smarts.push(s);
      });
    });

    console.log("SMARTS", [...smarts]);

    return this.save.execute({ agreementId, smarts: smarts });
  }

  publishOpportunity(agreementId: number, dataSource: GridRow2[]) {
    return this.publishOppo.execute({ agreementId });
  }

  loadAgreementData(agreementId: number, level: number) {
    return this.loadAgreementUseCase.execute({ agreementId, level });
  }
  editNotes(agreementId: number, notes: string) {
    return this.editNotesUseCase.execute({ agreementId, notes });
  }

  public updateFrozenCosts(agreementId: number, updatedCosts: any[]) {
    console.log("param", updatedCosts);
    return this.http.put(
      `${environment.apiUrl}/sm/agreement/${agreementId}/cost-freezes`,
      {
        costDtos: updatedCosts,
      },
    );
  }

  changeStatus(agreementId: number) {
    return this.changeStatusUseCase
      .execute({ agreementId })
      .pipe(
        catchError((err: HttpErrorResponse) => throwError(() => err.message)),
      );
  }

  getTravelAndExternalMonthlyCost(
    agreementId: number,
  ): Observable<MonthlyCostNew[]> {
    const typeOrder = { external: 1, travel: 2, labs: 3 };
    return this.http
      .get<{
        cost: MonthlyCostNew[];
      }>(`${this.endpoint}/sm/agreement/${agreementId}/costs`)
      .pipe(
        map((res) =>
          res.cost.sort((a, b) => typeOrder[a.type] - typeOrder[b.type]),
        ),
      );

    // {
    //   area: "Sustainability & Business Innovation / Energy and Resource Efficiency",
    //   disciplineId: 2,
    //   costs: [
    //     { monthId: "01 2025", externalCost: 0, travelCost: 0 },
    //     { monthId: "02 2025", externalCost: 0, travelCost: 0 },
    //     { monthId: "03 2025", externalCost: 0, travelCost: 0 },
    //   ],
    // },

    // return this.http.get("");
  }

  saveOtherCosts(
    startingList: MonthlyCostNew[],
    agreementId: number,
    agreementLevel: number,
  ) {
    const saveRequest = this.sumOtherCosts(
      startingList,
      agreementId,
      agreementLevel,
    );

    return this.http.put<{ response: boolean }>(
      `${this.endpoint}/sm/agreement/${agreementId}/costs`,
      {
        costDtos: saveRequest,
      }
    );
  }
  sumOtherCosts(
    startingList: MonthlyCostNew[],
    agreementId: number,
    agreementLevel: number,
  ) {
    const saveRequest: SaveOtherCostRequest[] = [];
    for (let i = 0; i < startingList.length; i++) {
      let curr = startingList[i];

      for (let j = 0; j < curr.costs.length; j++) {
        saveRequest.push({
          agreementId,
          agreementLevel,
          disciplineId: curr.disciplineId,
          type: curr.type,
          yearMonth: curr.costs[j].yearMonth,
          value: curr.costs[j].value,
        });
      }
    }
    return saveRequest;
  }

  /**
   * Fetches milestones for a specific agreement
   * @param agreementId The ID of the agreement
   * @returns Observable<Milestone[]> Array of milestones for the agreement
   */
  getMilestones(agreementId: number): Observable<Milestone[]> {
    return this.http.get<{milestones: Milestone[], errors: any}>(`${environment.apiUrl}/sm/agreement/${agreementId}/milestones`).pipe(
      map(response => {
        // Log the raw response to check the structure
        console.log('Raw API response for milestones:', response);
        
        const milestones = response.milestones || [];
        console.log('Milestones count from API:', milestones.length);
        
        // Check if there are suppliers in the response
        let totalSuppliers = 0;
        milestones.forEach(m => {
          const suppliersArray = m.suppliers || [];
          totalSuppliers += suppliersArray.length;
        });
        console.log('Total suppliers from API:', totalSuppliers);
        
        // Process each milestone to handle currency fields correctly
        return milestones.map(milestone => {
          // Create a normalized version of the milestone
          const normalizedMilestone: Milestone = {
            ...milestone,
          };

          // Gestione solo currencyCode
          if (!milestone.currencyCode) {
            console.log(`Milestone ${milestone.id}: No currencyCode specified, defaulting to EUR`);
            normalizedMilestone.currencyCode = 'EUR';
          } else {
            normalizedMilestone.currencyCode = milestone.currencyCode;
          }
          
          // Process suppliers if they exist
          if (milestone.suppliers && Array.isArray(milestone.suppliers)) {
            normalizedMilestone.suppliers = milestone.suppliers.map((rawSupplier: any) => {
              // Map backend supplier model to frontend supplier model
              return {
                id: rawSupplier.id,
                supplier: rawSupplier.supplierName || '', // backend uses supplierName, frontend uses supplier
                scope: rawSupplier.supplyScope || '',
                cost: rawSupplier.supplyCost || 0,
                startDate: rawSupplier.supplyStartDate,
                endDate: rawSupplier.supplyEndDate,
                status: rawSupplier.supplyStatus || 0,
                notes: rawSupplier.notes || '',
                currencyCode: rawSupplier.currencyCode || 'EUR'
              };
            });
            console.log(`Milestone ${milestone.id}: Processed ${normalizedMilestone.suppliers.length} suppliers`);
          } else {
            normalizedMilestone.suppliers = [];
            console.log(`Milestone ${milestone.id}: No suppliers found`);
          }
          
          return normalizedMilestone;
        });
      }),
      catchError((err: HttpErrorResponse) => {
        // Handle 404 (Not Found) errors - return empty array instead of throwing an error
        if (err.status === 404) {
          console.log('No milestones found for agreement ID:', agreementId);
          return of([]);
        }
        // For other errors, forward the full error object so consumers can check status
        return throwError(() => err);
      }),
    );
  }
  
  /**
   * Creates a new supplier for a specific milestone
   * @param agreementId The ID of the agreement
   * @param milestoneId The ID of the milestone
   * @param supplier The supplier data to create
   * @returns Observable<any> Response from the server
   */
  createMilestoneSupplier(agreementId: number, milestoneId: number, supplier: {
    supplierName: string;
    supplyScope: string;
    supplyCost: number;
    currencyCode: string;
    supplyStartDate: Date | string | null;
    supplyEndDate: Date | string | null;
    supplyStatus: string;
    notes?: string;
    createUser?: string;
  }): Observable<any> {
    const resourceId = this.userService.profile().resourceId?.toString() || 'WebUI';
    
    // Format dates if they are Date objects
    const formattedSupplier = {
      ...supplier,
      createUser: resourceId, // Ensure createUser is set with resource ID
      supplyStartDate: supplier.supplyStartDate instanceof Date 
        ? supplier.supplyStartDate.toISOString().split('T')[0] 
        : supplier.supplyStartDate,
      supplyEndDate: supplier.supplyEndDate instanceof Date 
        ? supplier.supplyEndDate.toISOString().split('T')[0] 
        : supplier.supplyEndDate
    };
    
    console.log('Create supplier request payload:', formattedSupplier);
    
    return this.http.post<any>(
      `${environment.apiUrl}/sm/agreement/${agreementId}/milestone/${milestoneId}/supplier`,
      formattedSupplier
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        // Handle 404 (Not Found) errors - likely milestone doesn't exist
        if (err.status === 404) {
          console.log('Cannot create supplier for milestone ID:', milestoneId, '- milestone not found');
          return throwError(() => err);
        }
        // For other errors, forward the full error object so consumers can check status
        return throwError(() => err);
      }),
    );
  }
  
  /**
   * Updates an existing supplier for a specific milestone
   * @param agreementId The ID of the agreement
   * @param milestoneId The ID of the milestone
   * @param supplierId The ID of the supplier to update
   * @param supplier The updated supplier data
   * @returns Observable<any> Response from the server
   */
  updateMilestoneSupplier(agreementId: number, milestoneId: number, supplierId: number, supplier: {
    supplierName: string;
    supplyScope: string;
    supplyCost: number;
    currencyCode: string;
    supplyStartDate: Date | string | null;
    supplyEndDate: Date | string | null;
    supplyStatus: string;
    notes?: string;
    createUser?: string;
    updateUser?: string;
  }): Observable<any> {
    const resourceId = this.userService.profile().resourceId?.toString() || 'WebUI';
    
    // Format dates if they are Date objects
    const formattedSupplier = {
      ...supplier,
      updateUser: resourceId, // Add updateUser with resource ID
      supplyStartDate: supplier.supplyStartDate instanceof Date 
        ? supplier.supplyStartDate.toISOString().split('T')[0] 
        : supplier.supplyStartDate,
      supplyEndDate: supplier.supplyEndDate instanceof Date 
        ? supplier.supplyEndDate.toISOString().split('T')[0] 
        : supplier.supplyEndDate
    };
    
    console.log('Update supplier request payload:', formattedSupplier);
    
    return this.http.put<any>(
      `${environment.apiUrl}/sm/agreement/${agreementId}/milestone/${milestoneId}/supplier/${supplierId}`,
      formattedSupplier
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        // Handle 404 (Not Found) errors - either milestone or supplier doesn't exist
        if (err.status === 404) {
          console.log(`Cannot update supplier ID: ${supplierId} for milestone ID: ${milestoneId} - resource not found`);
          return throwError(() => err);
        }
        // For other errors, forward the full error object so consumers can check status
        return throwError(() => err);
      }),
    );
  }


    /**
     * Crea un nuovo messaggio nel forum di una milestone
     * @param milestoneId ID della milestone
     * @param CreateUserId ID della risorsa
     * @param message Messaggio da inviare
     * @param supplierName Nome del fornitore (opzionale)
     * @param supplierCost Costo del fornitore (opzionale)
     * @param supplyStartDate Data inizio fornitura (opzionale)
     * @param supplyEndDate Data fine fornitura (opzionale)
     * @returns Observable<ForumMessageDto>
     */
    createForumMessage(
        milestoneId: number,
        message: string,
        CreateUserId: number,
        supplierName?: string,
        supplierCost?: number,
        currencyCode?: string,
        supplyStartDate?: string,
        supplyEndDate?: string
    ): Observable<ForumMessage> {
        const payload = {
            message,
            CreateUserId,
            supplierName,
            supplierCost,
            currencyCode,
            supplyStartDate: supplyStartDate ? new Date(supplyStartDate).toISOString().split('T')[0] : undefined,
            supplyEndDate: supplyEndDate ? new Date(supplyEndDate).toISOString().split('T')[0] : undefined
        };

        return this.http.post<{ message: ForumMessage }>(
            `${environment.apiUrl}/sm/milestone/${milestoneId}/message`,
            payload
        ).pipe(
            map(response => response.message),
            catchError((err: HttpErrorResponse) => {
                // Handle 404 (Not Found) errors - likely milestone doesn't exist
                if (err.status === 404) {
                    console.log('Cannot create forum message for milestone ID:', milestoneId, '- milestone not found');
                    return throwError(() => err);
                }
                // For other errors, forward the full error object so consumers can check status
                return throwError(() => err);
            })
        );
    }

    /**
     * Recupera tutti i messaggi del forum per una milestone
     * @param milestoneId ID della milestone
     * @returns Observable<ForumMessage[]>
     */
    getForumMessages(milestoneId: number): Observable<ForumMessage[]> {
        return this.http.get<{ messages: any[], errors?: any }>(
            `${environment.apiUrl}/sm/milestone/${milestoneId}/messages`
        ).pipe(
            map(response => {
                console.log('Raw forum messages response:', response);

                const rawMessages = response.messages || [];

                return rawMessages.map((rawMessage: any): ForumMessage => {
                    // Converti createDate da UTC a data locale dell'utente
                    const createDate = this.convertUtcToLocalTime(rawMessage.createDate);

                    return {
                        id: rawMessage.id,
                        milestoneId: rawMessage.milestoneId,
                        message: rawMessage.message,
                        supplierName: rawMessage.supplierName,
                        supplierCost: rawMessage.supplierCost,
                        currencyCode: rawMessage.currencyCode,
                        supplyStartDate: rawMessage.supplyStartDate,
                        supplyEndDate: rawMessage.supplyEndDate,
                        createDate: rawMessage.createDate,
                        createUserId: rawMessage.createUserId || 0,
                        user: rawMessage.user || null,

                        // Campi calcolati - APPLICA LE STESSE REGOLE DELL'APP.COMPONENT.TS
                        author: rawMessage.user?.name ?
                            `${rawMessage.user.name} ${rawMessage.user.surname || ''}`.trim() :
                            rawMessage.createUser || `User ${rawMessage.createUserId || 'Unknown'}`,
                        role: rawMessage.user?.roleDescription || rawMessage.user?.discipline || 'User',
                        date: createDate,
                        text: rawMessage.message
                    };
                });
            }),
            catchError((err: HttpErrorResponse) => {
                console.error('Error fetching forum messages:', err);
                if (err.status === 404) {
                    return of([]);
                }
                return throwError(() => err);
            })
        );
    }

    /**
     * Converte una data UTC nel fuso orario locale dell'utente
     * @param utcDateString Stringa data in formato UTC
     * @returns Date Oggetto Date nel fuso orario locale
     */
    private convertUtcToLocalTime(utcDateString: string): Date {
        // Il server invia sempre date in UTC, quindi assicuriamoci che sia trattata come tale
        const utcDate = new Date(utcDateString + (utcDateString.includes('Z') ? '' : 'Z'));
        
        // JavaScript convertir� automaticamente nel fuso orario locale quando viene visualizzata
        // Ma per essere sicuri, creiamo una nuova Date che rappresenta il momento locale
        return new Date(utcDate.getTime());
    }

    enrichMilestonesWithForumMessages(milestones: Milestone[]): Observable<Milestone[]> {
        if (!milestones || milestones.length === 0) {
            return of(milestones);
        }

        interface ForumMessageCount {
            milestoneId: number;
            messagesCount: number;
        }

        const forumMessagesRequests = milestones.map(milestone => 
            this.getForumMessages(milestone.id).pipe(
                map(messages => ({
                    milestoneId: milestone.id,
                    messagesCount: messages.length
                } as ForumMessageCount)),
                catchError(() => of({
                    milestoneId: milestone.id,
                    messagesCount: 0
                } as ForumMessageCount))
            )
        );

        return forkJoin(forumMessagesRequests).pipe(
            map((forumMessagesCounts: ForumMessageCount[]) => {
                return milestones.map(milestone => {
                    const messageCount = forumMessagesCounts.find(
                        (count: ForumMessageCount) => count.milestoneId === milestone.id
                    );
                    return {
                        ...milestone,
                        forumMessagesCount: messageCount ? messageCount.messagesCount : 0
                    };
                });
            })
        );
    }

    /**
     * Recupera tutte le valute disponibili dal database
     * @returns Observable<Currency[]> Array delle valute disponibili
     */
    getCurrencies(): Observable<Currency[]> {
        return this.http.get<{ currencies: Currency[] }>(
            `${environment.apiUrl}/sm/currencies`
        ).pipe(
            map(response => response.currencies || []),
            catchError((err: HttpErrorResponse) => {
                // Handle 404 (Not Found) errors - return empty array instead of throwing an error
                if (err.status === 404) {
                    console.log('No currencies found in the system');
                    return of([]);
                }
                // Forward the full error object so consumers can check status
                return throwError(() => err);
            })
        );
    }

    /**
     * Creates a new milestone for an agreement
     * @param agreementId The ID of the agreement
     * @param milestone The milestone data to create
     * @returns Observable<Milestone> The created milestone
     */
    createMilestone(agreementId: number, milestone: Partial<Milestone>): Observable<Milestone> {
        const resourceId = this.userService.profile().resourceId?.toString() || 'WebUI';
        
        // Ensure we're sending currency to the backend
        const requestPayload = {
            ...milestone,
            // Make sure currency field is properly set
            currencyCode: milestone.currencyCode || 'EUR',
            createUser: resourceId // Add createUser with resource ID
        };
        
        // Convert ItemStatus to BackendMilestoneStatus (string to number)
        if (typeof requestPayload.status === 'string') {
            switch (requestPayload.status) {
                case 'unknown':
                    requestPayload.status = 0;
                    break;
                case 'to-be-started':
                    requestPayload.status = 1;
                    break;
                case 'on-going':
                    requestPayload.status = 2;
                    break;
                case 'completed':
                    requestPayload.status = 3;
                    break;
                case 'on-hold':
                    requestPayload.status = 4;
                    break;
                default:
                    requestPayload.status = 0;
            }
        }
        
        console.log('Create milestone request payload:', requestPayload);
        
        return this.http.post<Milestone>(
            `${environment.apiUrl}/sm/agreement/${agreementId}/milestone`,
            requestPayload
        ).pipe(
            catchError((err: HttpErrorResponse) => {
                // Handle 404 (Not Found) errors - likely agreement doesn't exist
                if (err.status === 404) {
                    console.log('Cannot create milestone for agreement ID:', agreementId, '- agreement not found');
                }
                // Forward the full error object so consumers can check status
                return throwError(() => err);
            })
        );
    }

    /**
     * Updates an existing milestone
     * @param agreementId The ID of the agreement
     * @param milestoneId The ID of the milestone to update
     * @param milestone The updated milestone data
     * @returns Observable<Milestone> The updated milestone
     */
    updateMilestone(agreementId: number, milestoneId: number, milestone: Partial<Milestone>): Observable<Milestone> {
        const resourceId = this.userService.profile().resourceId?.toString() || 'WebUI';
        
        // Ensure we're sending currency to the backend (not currencyCode)
        const requestPayload = {
            ...milestone,
            // Make sure currency field is properly set
            currencyCode: milestone.currencyCode || 'EUR',
            updateUser: resourceId // Add updateUser with resource ID
        };
        
        // Convert ItemStatus to BackendMilestoneStatus (string to number)
        if (typeof requestPayload.status === 'string') {
            switch (requestPayload.status) {
                case 'unknown':
                    requestPayload.status = 0;
                    break;
                case 'to-be-started':
                    requestPayload.status = 1;
                    break;
                case 'on-going':
                    requestPayload.status = 2;
                    break;
                case 'completed':
                    requestPayload.status = 3;
                    break;
                case 'on-hold':
                    requestPayload.status = 4;
                    break;
                default:
                    requestPayload.status = 0;
            }
        }
        
        console.log('Update milestone request payload:', requestPayload);
        
        return this.http.put<Milestone>(
            `${environment.apiUrl}/sm/agreement/${agreementId}/milestone/${milestoneId}`,
            requestPayload
        ).pipe(
            catchError((err: HttpErrorResponse) => {
                // Handle 404 (Not Found) errors - either agreement or milestone doesn't exist
                if (err.status === 404) {
                    console.log(`Cannot update milestone ID: ${milestoneId} for agreement ID: ${agreementId} - resource not found`);
                }
                // Forward the full error object so consumers can check status
                return throwError(() => err);
            })
        );
    }

    /**
     * Deletes a milestone
     * @param agreementId The ID of the agreement
     * @param milestoneId The ID of the milestone to delete
     * @returns Observable<any> Response from the server
     */
    deleteMilestone(agreementId: number, milestoneId: number): Observable<any> {
        const resourceId = this.userService.profile().resourceId?.toString() || 'WebUI';
        return this.http.delete(
            `${environment.apiUrl}/sm/agreement/${agreementId}/milestone/${milestoneId}/${resourceId}`
        ).pipe(
            catchError((err: HttpErrorResponse) => {
                // Handle 404 (Not Found) errors - either agreement or milestone doesn't exist
                // In this case, consider deletion successful even if the resource doesn't exist
                if (err.status === 404) {
                    console.log(`Cannot delete milestone ID: ${milestoneId} for agreement ID: ${agreementId} - resource not found`);
                    // Return an empty successful response since the resource is already gone
                    return of({ success: true });
                }
                // Forward the full error object for other errors
                return throwError(() => err);
            })
        );
    }

    /**
     * Deletes a milestone supplier
     * @param agreementId The ID of the agreement
     * @param milestoneId The ID of the milestone
     * @param supplierId The ID of the supplier to delete
     * @returns Observable<any> Response from the server
     */
    deleteMilestoneSupplier(agreementId: number, milestoneId: number, supplierId: number): Observable<any> {
        const resourceId = this.userService.profile().resourceId?.toString() || 'WebUI';
        return this.http.delete(
            `${environment.apiUrl}/sm/agreement/${agreementId}/milestone/${milestoneId}/supplier/${supplierId}/${resourceId}`
        ).pipe(
            catchError((err: HttpErrorResponse) => {
                // Handle 404 (Not Found) errors - either agreement, milestone or supplier doesn't exist
                // In this case, consider deletion successful even if the resource doesn't exist
                if (err.status === 404) {
                    console.log(`Cannot delete supplier ID: ${supplierId} for milestone ID: ${milestoneId} - resource not found`);
                    // Return an empty successful response since the resource is already gone
                    return of({ success: true });
                }
                // Forward the full error object for other errors
                return throwError(() => err);
            })
        );
    }

    /**
     * Checks if the agreement has minimum timesheet hours per discipline (100+ hours)
     * @param agreementId The ID of the agreement
     * @returns Observable<boolean> True if at least one discipline has 100+ hours
     */
    checkMinimumTimesheetHours(agreementId: number): Observable<boolean> {
        return this.http.get<{hasMinimumHours: boolean, errors?: any}>(`${environment.apiUrl}/sm/agreement/${agreementId}/check-timesheet-hours`).pipe(
            map(response => response.hasMinimumHours || false),
            catchError((err: HttpErrorResponse) => {
                console.error('Error checking timesheet hours:', err);
                // Return false as default if the check fails
                return of(false);
            })
        );
    }
    /**
     * Check if the agreement has any timesheet hours for at least one resource
     * @param agreementId The ID of the agreement to check
     * @returns Observable<boolean> True if at least one resource has any hours
     */
    checkHasAnyTimesheetHours(agreementId: number): Observable<boolean> {
        return this.http.get<{hasAnyHours: boolean, errors?: any}>(`${environment.apiUrl}/sm/agreement/${agreementId}/check-any-timesheet-hours`).pipe(
            map(response => response.hasAnyHours || false),
            catchError((err: HttpErrorResponse) => {
                console.error('Error checking any timesheet hours:', err);
                // Return false as default if the check fails
                return of(false);
            })
        );
    }}

export type costType = "external" | "travel" | "labs";

export interface SaveOtherCostRequest {
  disciplineId: number;
  agreementId: number;
  agreementLevel: number;
  type: costType;
  yearMonth: string;
  value: number;
}

export interface Currency {
    code: string;
    description: string;
    character: string;
}
